export interface AppRoute {
  title: string;
  primary: boolean;
  path: string;
  exact?: boolean;
  external?: boolean;
  component?: JSX.Element;
  icon?: JSX.Element;
}

export function cast<T>(data: any) {
  return data as T;
}

export interface APIEndpoint {
  key?: string;
  path: string;
  method: string;
  typeIn?: (data: any) => any;
  typeOut?: (data: any) => any;
  pathParams?: string[];
  queryParams?: string[];
}

export interface RequestInput {
  body?: any;
  pathParams?: { [name: string]: string };
  queryParams?: { [name: string]: string };
}

export type User = {
  email: string;
  password: string;
  salt: string;
  name: string;
  is_disabled: boolean;
  dnatest_report: string;
  status: string;
  is_dna_section_enabled: boolean;
  is_blood_section_enabled: string;
  extra_user_data: string;
  report_filename: string;
  completed_date: Date;
  is_order_vitamins_offered: boolean;
  is_submitted_to_order_manager: boolean;
  user_type: string;
  user_report_id: number;
  is_show_popup_referral: boolean;
  partner: number;
  first_name: string;
  last_name: string;
  complete_raw_genetic_data: string;
  login_options: string;
  locked_time: Date;
  failed_count: number;
  date_upload_blood: Date;
  first_login: boolean;
  phone: string;
};

export type UserNutrient = {
  blood_result: string;
  blood_result_text: string;
  cortisol_result: string;
  cortisol_result_text: string;
  dna_result: string;
  dna_result_text: string;
  id: number;
  mg: number;
  nutrient: number;
  questionnaire_result: string;
  questionnaire_result_text: string;
  score: number;
  text: string;
  user: number;
};

export type UserAuth = {
  info?: User;
  token?: string;
  session_id?: string; // used for otp implemetation
  email?: string; // used for otp implemetation
  code?: string; // used for otp implemetation
  message?: string; // used for otp implemetation
  error?: string;
};

export type UserGetIn = {
  email: string;
  token: string;
};

export type UserGetOut = {
  id: string;
  blood_result: boolean;
  customer_status: string;
  customer_type: string;
  dna_result: boolean;
  dnatest_report: string;
  dna_status: string;
  email: string;
  first_name: string;
  hormone_status: string;
  hormone_subscription_status: string;
  is_dna_section_available: boolean;
  last_name: string;
  name: string;
  omega3_status: string;
  phone: string;
  ship_street1: string;
  ship_street2: string;
  ship_city: string;
  ship_state: string;
  ship_zip: string;
  subscription_status: string;
  vitamin_test_status: string;
  vitamins_in_shipping: boolean;
  wearable_source_id: string;
  dob?: string;
  biological_sex?: string;
};

export type UserUpdateIn = {
  first_name: string;
  last_name: string;
  address: Address;
};

export type UserJourney = {
  id: string;
  user_id: string;
  key: string;
  journey_id: string;
  name: string;
  description: string;
  order: string;
  optional: boolean;
  status: string;
  assigned: string; // datetime
  completed: string; // datetime
};

export type UserRequest = {
  request_type: string;
  message: string;
};

export type UserJourneyGetIn = {
  email: string;
  token: string;
  _sort_key: string;
  _sort_dir: string;
};

export type UserNutrientSummary = {
  nutrient_id?: string;
  icon?: string;
  title?: string;
  description?: string;
  text?: string;
  value?: string;
  mg?: string;
  value_mg?: string;
  unit?: string;
  group?: string;
  questionnaire_result?: string;
  questionnaire_result_text?: string;
  dna_result?: string;
  dna_result_text?: string;
  blood_result?: string;
  blood_result_text?: string;
  cortisol_result?: string;
  cortisol_result_text?: string;
  based_on?: string;
  text_formula?: string;
  about_the_vitamin?: string;
  did_you_know?: string;
};

export type BloodResult = {
  id: string;
  nutrient_id: string;
  nutrient_name: string;
  nutrient_value_unit: string;
  nutrient_value: string;
  nutrient_level: string;
  nutrient_group: string;
  nutrient_unit: string;
};

export type UserBloodResultsOut = {
  id: string;
  user_vitamins: BloodResult[];
};

export type BloodTestQuestion = {
  title: string;
  code: string;
  sort_order: number;
  min_value: number;
  max_value: number;
  min_value_out_of_range_error_message: string;
  max_value_out_of_range_error_message: string;
  question_id: string;
  result?: string;
  result_id?: string;
};

export type UserBloodUploadOut = {
  id: string;
  answers: BloodTestQuestion[];
};

export type BloodTestQuestionIn = {
  id?: string;
  value: string;
  question_id: string;
};

export type UserBloodUploadIn = {
  answer: BloodTestQuestionIn[];
};

export type GenerateShareLinkOut = {
  link: string;
};

export type TrackedOrder = {
  product_name: string;
  ship_date: string;
  expected_delivery_date: string;
  ship_from_city: string;
  ship_from_state: string;
  ship_to_city: string;
  ship_to_state: string;
  status: string;
  tracking: string;
};

export type AnalyteResult = {
  date?: string;
  result: number;
};

export type AnalyteReport = {
  id: number;
  name: string;
  analyte_code: string;
  description: string;
  test_type: string;
  nutrients_impacted: string;
  unit: string;
  min_value: number;
  max_value: number;
  algorithm_min_value: number;
  algorithm_max_value: number;
  algorithm_optimal_value: number;
  optimal_min_value: number;
  optimal_max_value: number;
  sort_order: number;
  rootine_enabled: boolean;
  result_history: AnalyteResult[];
};

export type UserDnaCategory = {
  id: number;
  code: string;
  name: string;
  result_text_header: string;
  description: string;
  result_text: string;
  amount_recommended: string;
  scale: number;
  related_nutrients: string[];
  influential_genes: string[];
  scientific_papers: string[];
};

export type HormoneTest = {
  type: string;
  label: string;
  unit: string;
  min_value: number;
  max_value: number;
  optimal_min_value: number;
  optimal_max_value: number;
};

export type HormoneTestResult = {
  hormone_test: HormoneTest;
  result: number;
  result_level: string;
  min_value: number;
  max_value: number;
  sample_datetime: string;
};

export type HormoneSnapshot = {
  result: string;
  sample_date: string;
  test_results: HormoneTestResult[];
};

export type HormoneReport = {
  test_name: string;
  test_description?: string;
  snapshots: HormoneSnapshot[];
};

export type CortisolCopy = {
  result: string;
  result_text: string;
  label: string;
  description: string;
  recovery_detail: string;
  overview_video?: string;
  overview_gif?: string;
  chart_video?: string;
  chart_gif?: string;
  symptoms_video?: string;
  symptoms_gif?: string;
  recommendations_video?: string;
  recommendations_gif?: string;
  what_to_expect: string;
  checkup: string;
  symptoms: Array<{
    text: string;
    detail?: string;
    icon?: string;
  }>;
  recommendation_header: string;
  recommendations: Array<{
    name: string;
    label: string;
    positive: Array<{
      text: string;
      icon: string;
    }>;
    negative: Array<{
      text: string;
      icon: string;
    }>;
  }>;
};

export enum ResultType {
  Normal = 'normal',
  Alarmed = 'type_1',
  Imbalanced = 'type_2',
  Exhausted = 'type_3',
}

export type CortisolResult = {
  time: string;
  result: number;
  low: number;
  average: number;
  high: number;
  diff?: number;
};

export enum StressInsightsColors {
  NORMAL = '#2AD033',
  ALARMED = '#FFD233',
  IMBALANCED = '#FF7C33',
  EXHAUSTED = '#EF562B',
}

export type ProductOut = {
  code: string;
  price: number;
  interval_length?: number;
  interval_unit?: string;
  total_billing_cycles?: number;
};

export type PaymentMethod = {
  card_type?: string;
  first_six?: string;
  last_four?: string;
  last_two?: string;
  exp_month?: string;
  exp_year?: string;
  gateway_token?: string;
  cc_bin_country?: string;
  gateway_code?: string;
  billing_agreement_id?: string;
  name_on_account?: string;
  account_type?: string;
  routing_int?: string;
  routing_int_bank?: string;
  username?: string;
};

export type Address = {
  id?: string;
  object?: string;
  account_id?: string;
  nickname?: string;
  first_name?: string;
  last_name?: string;
  company?: string;
  email?: string;
  vat_int?: string;
  phone?: string;
  street1?: string;
  street2?: string;
  city?: string;
  region?: string;
  postal_code?: string;
  country?: string;
  created_at?: string;
  updated_at?: string;
};

export type BillingInfo = {
  account_id?: string;
  first_name?: string;
  last_name?: string;
  address?: Address;
  payment_method?: PaymentMethod;
};

export type Subscription = {
  id: number;
  customer_id: number;

  product_code: string;
  recurly_account_code: string;
  recurly_id: string;
  recurly_uuid: string;
  state: string;
  current_period_started_at: string;
  current_period_ends_at: string;
  current_term_started_at: string;
  current_term_ends_at: string;
  remaining_billing_cycles: number;
  total_billing_cycles: number;
  renewal_billing_cycles: number;
  auto_renew: boolean;
  paused_at: string;
  remaining_pause_cycles: number;
  unit_amount: number;
  quantity: number;
  subtotal: number;
  total: number;
  expiration_reason: string;
  created_at: string;
  updated_at: string;
  activated_at: string;
  canceled_at: string;
  expires_at: string;
  active_invoice_id: string;
};

export enum SubscriptionRequestType {
  pause = 'pause',
  cancel = 'cancel',
}

export type UserSubscriptionRequest = {
  subscription_id: number;
  type: SubscriptionRequestType;
  reason: string;
  detail: string;
  pause_length?: number;
};

export type AdminConfig = {
  select_lists: Map<string, string[]>;
};

export enum DataType {
  Date = 'date',
  Select = 'select',
  LongText = 'longtext',
}

export type ListResource = {
  total: number;
  records: any;
};

export type LoginRequest = {
  username: string;
  mobile?: boolean;
};

export type QuestionOption = {
  title: string;
  value: string;
  subtitle?: string;
};

export type QuestionData = {
  options: QuestionOption[];
  text?: string;
};

export type DependentQuestion = {
  parent_question_id: number;
  operator: '=' | '!=' | 'includes' | 'not includes';
  value: string;
  child_question_id: number;
};

export type Question = {
  id: number;
  title: string;
  kind: string;
  category?: string;
  subtitle?: string;
  code: string;
  value?: string;
  is_active: boolean;
  data?: QuestionData;
  quiz_id: number;
  required: boolean;
  include_other: boolean;
  min_amount?: number;
  max_amount?: number;
  regex?: RegExp;
  error_message?: string;
  render?: boolean;
  dependencies?: DependentQuestion[];
};

export type Quiz = {
  id: number;
  title: string;
  description?: string;
  active: boolean;
  code: string;
  version: number;
  questions?: Question[];
  end_message?: string;
  end_link?: string;
  end_button_text?: string;
};

export type QuizResponseData = {
  question_id: number;
  session?: string;
  user_id?: string;
  answer: string;
  quiz_id: number;
};

export type QuizResponse = {
  response: QuizResponseData;
  percent_completed: number;
  is_update: boolean;
};

export type ReviewAuthor = {
  name: string;
  title?: string;
};

export type Review = {
  rating: number;
  review: string;
  author: ReviewAuthor;
  mediaSrc?: string;
};

export type AnalyticsData = {
  eventName?: string;
  data: {};
};

export type HormoneAlgoInput = {
  [key: string]: {
    name: string;
    test_type: string;
    min_value: number;
    max_value: number;
    value: number;
    unit: string;
  }[];
};

export type DnaAlgoInputItem = {
  id: number;
  rsid: string;
  chromosome: string;
  position: string;
  genotype: string;
  gene_convert: string;
  is_convert: 'string';
  external_user: string;
  user_id: number;
};

export type BloodAlgoInput = { [key: string]: number };

export type UserAlgoInput = {
  user_id: number;
  height_feet: number;
  height_inch: number;
  weight_lbs: number;
  birth_year: number;
  birth_day: number;
  birth_month: number;
  gender: 'm' | 'f';
};

export type LifestyleAlgoInput = {
  dna_file: string;
  energy_level: string;
  gluten_free: string;
  sleep: string;
  dairy_consumption: string;
  nutrition_type: string;
  focus_level: string;
  gender: string;
  smoking: string;
  sun_exposure: string;
  email: string;
  mood_level: string;
  fruit_veg_ingestion: string;
  fish_consumption: string;
  free_time_exercise: string;
  exercise_amount_at_work: string;
  stress_level: string;
  optimize_goal: string;
};

export type AlgoInput = {
  data: {
    job_id: number;
    user: UserAlgoInput;
    blood: BloodAlgoInput;
    dna: DnaAlgoInputItem[];
    hormone: HormoneAlgoInput;
    lifestyle: LifestyleAlgoInput;
    callback?: string;
  };
  legacy: any;
};

export type GeneCategoryOut = {
  name: any;
  genes: string[];
  coded_result: Record<string, any>;
  use_results_scale: boolean;
  result: number;
  result_text_map: Record<number, string>;
  result_text: string;
  category_min: number;
  category_max: number;
  default_coded_result: number;
  use_default_avg: boolean;
  avg_constants: Record<string, number>;
  high_risk_fold: number;
  multivariate: boolean;
  total_factor: number;
  scalebars: any;
  precision: number;
};

export type DosageOut = {
  name: string;
  label: string;
  min_amount?: number;
  max_amount?: number;
  avg_amount?: number;
  rda_age_cap?: number;
  factor?: number;
  min_factor?: number;
  mult_factor_rda?: boolean;
  use_reducer?: boolean;
  male_rda_base_amount?: number;
  male_age_rda_multiplier?: number;
  female_rda_base_amount?: number;
  female_age_rda_multiplier?: number;
  quiz_vars?: string[];
  blood_vars?: string[];
  dna_vars?: string[];
  high_dose_danger?: boolean;
  blood_result?: number;
  hormone_result?: number;
  dna_result?: number;
  dna_driver?: string;
  quiz_result?: number;
  quiz_driver?: string;
  final_range_percent?: number;
  final_result?: number;
  precision?: number;
  final_driver?: string;
  overrides_used?: string[];
};

export type AlgoOutput = {
  job_id?: number;
  user_id?: number;
  recommendations?: DosageOut[];
  cortisol_result?: string;
  dna_results?: GeneCategoryOut[];
  used_blood_results?: boolean;
  status?: string;
  error?: string;
};

export enum BloodAnalyte {
  Folicacid = 'Folicacid',
  VitB12 = 'VitB12',
  VitD3 = 'VitD3',
  HSCRP = 'HSCRP',
  HCY = 'HCY',
  Magnesium = 'Magnesium',
  Copper = 'Copper',
  Zinc = 'Zinc',
  Selenium = 'Selenium',
  Mercury = 'Mercury',
  Cadmium = 'Cadmium',
}

export type AnalyteData = {
  analyte_code: string;
  name: string;
  role_in_body?: string[];
  optimal_level_benefit?: string;
  food_sources?: string[];
  smart_multi_impact: string;
  reference_range: string;
  is_inflammatory_marker: boolean;
  is_heavy_metal: boolean;
  what_is_it_description?: string;
  impacting_factors?: string[];
  ways_to_optimize?: string[];
};

export type BloodRecommendation = {
  title: string;
  description?: string;
  secondary_description?: string;
  imgSrc?: string;
  goal?: string;
  link?: string;
};

export type BloodInsightRecommendationDetails = {
  label: string;
  subtitle: string;
  description: string;
  imgSrc?: string;
  isPurchaseRec: boolean;
  recs: BloodRecommendation[];
};

export type RsID = {
  gene?: string;
  result: string;
  rsID: string;
};

export type Gene = {
  id: number;
  name: string;
  label: string;
  result: string;
  description: string;
  health_relevance?: string;
  scientific_references?: string[];
  rsIDs?: RsID[];
};

export type ProductMappingDetails = {
  id: string;
  selling_plan: string;
};

export type ProductMappings = {
  [key: string]: ProductMappingDetails;
};
